var _excluded = ["accept", "className", "defaultValue", "disabled", "error", "i18n", "id", "label", "labelDragging", "multiple", "name", "onBlur", "onChange", "onClear", "onClick", "onDrop", "onFocus", "onMouseOut", "onMouseOver", "separateLabel", "value"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React from 'react';
import mergeRefs from 'react-merge-refs';
import TextBox from '../text_box';
import IconUpload from '../icons/icon_upload';
import IconX from '../icons/icon_x';
import useStyles from './use_styles';
import useControlled from '../utils/use_controlled';
import useOnClickOutside from '../utils/use_on_click_outside';
import UtilityButton from '../shared/utility_button';
import filePickeri18n from './i18n';
import { jsx as ___EmotionJSX } from "@emotion/core";
var FilePicker = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
      accept: acceptProp = '*.*',
      className,
      defaultValue,
      disabled = false,
      error = false,
      i18n,
      id,
      label = 'Upload file',
      labelDragging = 'Drop file here',
      multiple = false,
      name,
      onBlur,
      onChange,
      onClear,
      onClick,
      onDrop,
      onFocus,
      onMouseOut,
      onMouseOver,
      separateLabel = false,
      value: valueProp
    } = _ref,
    inputProps = _objectWithoutProperties(_ref, _excluded);
  var inputRef = React.useRef();
  var isFilled = Boolean(valueProp || defaultValue);
  var [state, setState] = React.useState({
    active: false,
    dragging: false,
    filled: isFilled,
    focused: false,
    hover: false
  });
  var [value, setValue] = useControlled({
    controlled: valueProp,
    default: defaultValue
  });
  var setInteractionState = React.useCallback((boolean, interaction) => {
    setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
      [interaction]: boolean
    }));
  }, []);
  if (state.focused && disabled) {
    setInteractionState(false, 'focused');
  }
  var handleClickOutside = React.useCallback(() => {
    setInteractionState(false, 'focused');
  }, [setInteractionState]);
  var onClickOutside = useOnClickOutside({
    listener: handleClickOutside
  });
  var namesStrFromFileList = fileList => {
    if (!fileList) {
      return '';
    }
    return Array.from(fileList).map(file => file.name).join(', ');
  };
  var handleClick = React.useCallback(event => {
    setInteractionState(true, 'focused');
    if (inputRef.current) {
      inputRef.current.click();
      inputRef.current.focus();
    }
    onClick === null || onClick === void 0 || onClick(event);
  }, [onClick, setInteractionState]);
  var handleChange = React.useCallback((_, event) => {
    if (!event.target.files || event.target.files.length === 0) return;
    var names = namesStrFromFileList(event.target.files);
    setValue(names);
    onChange === null || onChange === void 0 || onChange(event, event.target.files, names);
    setInteractionState(names !== '', 'filled');
    event.target.value = '';
  }, [onChange, setInteractionState, setValue]);
  var handleOnFocus = React.useCallback(event => {
    setInteractionState(true, 'focused');
    setInteractionState(true, 'hover');
    onFocus === null || onFocus === void 0 || onFocus(event);
  }, [onFocus, setInteractionState]);
  var handleOnBlur = React.useCallback(event => {
    setInteractionState(false, 'focused');
    setInteractionState(false, 'hover');
    onBlur === null || onBlur === void 0 || onBlur(event);
  }, [onBlur, setInteractionState]);
  var handleMouseOver = React.useCallback(event => {
    setInteractionState(true, 'hover');
    onMouseOver === null || onMouseOver === void 0 || onMouseOver(event);
  }, [onMouseOver, setInteractionState]);
  var handleMouseOut = React.useCallback(event => {
    setInteractionState(false, 'hover');
    onMouseOut === null || onMouseOut === void 0 || onMouseOut(event);
  }, [onMouseOut, setInteractionState]);
  var handleMouseDown = React.useCallback(() => {
    setInteractionState(true, 'active');
  }, [setInteractionState]);
  var handleMouseUp = React.useCallback(() => {
    setInteractionState(false, 'active');
  }, [setInteractionState]);
  var handleKeyDown = React.useCallback(event => {
    if (disabled) return;
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleClick(event);
        break;
      default:
        break;
    }
  }, [disabled, handleClick]);
  var handleDragOver = React.useCallback(event => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    setInteractionState(true, 'dragging');
  }, [setInteractionState, disabled]);
  var handleDragLeave = React.useCallback(event => {
    event.stopPropagation();
    setInteractionState(false, 'dragging');
  }, [setInteractionState]);
  var handleDrop = React.useCallback(event => {
    setInteractionState(true, 'focused');
    if (!onDrop || !event.dataTransfer.files || event.dataTransfer.files.length === 0) {
      return;
    }
    event.preventDefault();
    setInteractionState(false, 'dragging');
    var names = namesStrFromFileList(event.dataTransfer.files);
    setValue(names);
    setInteractionState(names !== '', 'filled');
    onDrop(event, event.dataTransfer.files, names);
  }, [onDrop, setInteractionState, setValue]);
  var handleClearMouseDown = React.useCallback(e => {
    e.preventDefault();
    setValue(undefined);
    setInteractionState(false, 'filled');
    onClear === null || onClear === void 0 || onClear(e);
  }, [onClear, setInteractionState, setValue]);
  var handleClearKeyDown = React.useCallback(e => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        e.stopPropagation();
        setValue(undefined);
        onClear === null || onClear === void 0 || onClear(e);
        break;
      default:
        break;
    }
  }, [disabled, onClear, setValue]);
  var handleInputRefs = mergeRefs([inputRef, ref, onClickOutside]);
  var iconUploadProps = {
    customStyle: {
      padding: 0
    },
    disabled,
    filled: false
  };
  var style = useStyles({
    active: state.active,
    disabled,
    dragging: state.dragging,
    error,
    filled: state.filled,
    focused: state.focused,
    hover: state.hover,
    separateLabel
  });
  var uploadButton = ___EmotionJSX("div", {
    "aria-label": (i18n === null || i18n === void 0 ? void 0 : i18n.upload) || filePickeri18n.upload,
    css: style.uploadButton,
    role: "button",
    tabIndex: disabled ? -1 : 0
  }, /*#__PURE__*/React.cloneElement(___EmotionJSX(IconUpload, null), iconUploadProps));
  var clearButton = state.filled && !disabled && ___EmotionJSX(UtilityButton, {
    "aria-label": (i18n === null || i18n === void 0 ? void 0 : i18n.clear) || filePickeri18n.clear,
    css: style.clearButton,
    onKeyDown: handleClearKeyDown,
    onMouseDown: handleClearMouseDown,
    tabIndex: disabled ? -1 : 0,
    icon: ___EmotionJSX(IconX, {
      "aria-hidden": true
    })
  });
  return ___EmotionJSX("div", {
    className: className,
    css: style.container,
    onBlur: handleOnBlur,
    onClick: handleClick,
    onDragLeave: handleDragLeave,
    onDragOver: onDrop !== undefined ? handleDragOver : undefined,
    onDrop: handleDrop,
    onFocus: handleOnFocus,
    onKeyDown: handleKeyDown,
    onMouseDown: handleMouseDown,
    onMouseOut: handleMouseOut,
    onMouseOver: handleMouseOver,
    onMouseUp: handleMouseUp,
    role: "none"
  }, ___EmotionJSX(TextBox, _extends({}, inputProps, {
    accept: acceptProp,
    css: style.root,
    disabled: disabled,
    error: error,
    files: value,
    id: id,
    label: state.dragging ? labelDragging : label,
    multiple: multiple,
    name: name,
    onChange: handleChange,
    ref: handleInputRefs,
    trailingIcon: uploadButton,
    type: "file",
    separateLabel: separateLabel
  })), clearButton);
});
FilePicker.displayName = 'FilePicker';
export default FilePicker;